// @ts-check
import { Button, Stack, StackItem, Text, TextInput, TextVariants } from '@patternfly/react-core';
import SimpleTable, { createDefaultActions } from 'components/Common/SimpleTable/SimpleTable';
import SelectV2 from 'components/Select/SelectV2';
import ModelContentSelectorModal from 'components/SignalSelector/ModelContentSelectorModal';
import { useTranslation } from 'localization/useTranslation';
import React, { useCallback, useMemo } from 'react';
import { ContentType } from 'utils/dtdl';
import useBoolean from 'utils/hooks/useBoolean';
import _ from 'lodash';
import Operator, { LogicalOperator, LogicalOperatorTranslation, OperatorTranslation } from '../Operator';

/**
 * @readonly
 * @type {import('components/Select/SelectV2').OptionType<Operator>[]}
 */
const OperatorOptions = Object.values(Operator).map(operator => ({
  text: OperatorTranslation[operator],
  value: operator
}));

/**
 * @readonly
 * @type {import('components/Select/SelectV2').OptionType<LogicalOperator>[]}
 */
const LogicalOperatorOptions = Object.values(LogicalOperator).map(logicalOperator => ({
  text: LogicalOperatorTranslation[logicalOperator],
  value: logicalOperator
}));

/**
 * @typedef {object} Condition
 * @property {import('utils/types').Property} property
 * @property {Operator} operator
 * @property {number} value
 * @property {LogicalOperator} logicalOperator
 */


const SelectedPropertyButton = ({ property, onClick }) => {
  return (
    <Stack>
      <StackItem><Text onClick={onClick} component={TextVariants.a}>{ property.displayName }</Text></StackItem>
      <StackItem><Text component={TextVariants.small}>{ property.modelId }</Text></StackItem>
    </Stack>
  );
};

/**
 * @typedef {object} PropertySelectorProps
 * @property {object} property
 * @property {Function} onPropertyChanged
 */


/**
 * @param {PropertySelectorProps} props
 * @returns {JSX.Element}
 */
const PropertySelector = ({ property, onPropertyChanged }) => {
  const T = useTranslation();

  const [isModalOpen, openModal, closeModal] = useBoolean(false);

  const _onChanged = useCallback((newProperties) => {
    onPropertyChanged(_.head(newProperties));
    closeModal();
  }, [closeModal, onPropertyChanged]);

  return (<>
    { property ? <SelectedPropertyButton property={property} onClick={openModal}/> :
      <Button onClick={openModal}>{ T.modals.selectproperties.title }</Button> }
    <ModelContentSelectorModal
      selectedItems={property ? [property] : []}
      isOpen={isModalOpen}
      onConfirm={_onChanged}
      onCancel={closeModal}
      contentType={ContentType.PROPERTIES}
      isSingleSelect
    />
  </>);
};

/**
 * @typedef {object} PropertiesConditionProps
 * @property {Condition[]} [conditions]
 * @property {(newConditions: Condition[]) => void} onConditionsChanged
 */

/**
 * @param {PropertiesConditionProps} props
 * @returns {JSX.Element}
 */
const PropertiesConditionEditor = ({ conditions, onConditionsChanged }) => {
  const T = useTranslation();
  const onLogicalOperatorChanged = useCallback((logicalOperator, index) => {
    const newConditions = [...conditions];
    newConditions[index].logicalOperator = logicalOperator;
    onConditionsChanged(newConditions);
  }, [conditions, onConditionsChanged]);

  const onOperatorChanged = useCallback((operator, index) => {
    const newConditions = [...conditions];
    newConditions[index].operator = operator;
    onConditionsChanged(newConditions);
  }, [conditions, onConditionsChanged]);

  const onValueChanged = useCallback((value, index) => {
    const newConditions = [...conditions];
    newConditions[index].value = _.toNumber(value);
    onConditionsChanged(newConditions);
  }, [conditions, onConditionsChanged]);

  const onPropertyChanged = useCallback((property, index) => {
    const newConditions = [...conditions];
    newConditions[index].property = property;
    onConditionsChanged(newConditions);
  }, [conditions, onConditionsChanged]);

  /**
   * @type {import('components/Common/SimpleTable/SimpleTable').ColumnType<Condition>[]}
   */
  const columns = useMemo(() => _.compact([
    conditions.length > 1 && {
      label: T.common.logicalOperatorTitle,
      path: 'logicalOperator',
      render: (logicalOperator, unused, index) => {
        if (index === 0) {
          // First index should not show any operator, it is always LogicalOperator.AND
          return null;
        }
        return (
          <SelectV2
            options={LogicalOperatorOptions}
            value={logicalOperator ?? LogicalOperator.AND} // fallback to AND for backward compatibility
            onChange={(logicalOperator) => onLogicalOperatorChanged(logicalOperator, index)}/>
        );
      }
    },
    {
      label: T.common.properties,
      path: 'property',
      render: (property, unused, index) => <PropertySelector property={property} onPropertyChanged={(property) => onPropertyChanged(property, index)}/>
    },
    {
      label: T.admin.alarms.rule.operator,
      path: 'operator',
      render: (operator, unused, index) => <SelectV2 options={OperatorOptions} value={operator} onChange={(operator) => onOperatorChanged(operator, index)} />
    },
    {
      label: T.common.propertyValue,
      path: 'value',
      render: (value, unused, index) => <TextInput type="number" aria-label="Value" value={value} onChange={(unused, value) => onValueChanged(value, index)} />
    }
  ]), [T.admin.alarms.rule.operator, T.common.logicalOperatorTitle, T.common.properties, T.common.propertyValue, conditions?.length, onLogicalOperatorChanged, onOperatorChanged, onPropertyChanged, onValueChanged]);

  const onDelete = useCallback((unused, index) => {
    const newConditions = [...conditions];
    newConditions.splice(index, 1);
    onConditionsChanged(newConditions);
  }, [conditions, onConditionsChanged]);

  const actions = useMemo(() => createDefaultActions({ onDelete, isOverflow: true, useIcons: true }), [onDelete]);

  /**
   * @type {import('components/Common/SimpleTable/SimpleTable').GlobalAction[]}
   */
  const globalActions = [
    {
      onClick: () => {
        onConditionsChanged([...(conditions ?? []), { property: null, operator: Operator.EQUAL, value: 0, logicalOperator: LogicalOperator.AND }]);
      }
    }
  ];

  return (<SimpleTable
    columns={columns}
    data={conditions}
    globalActions={globalActions}
    actions={actions}
    compact
  />);
};

export default PropertiesConditionEditor;
